import { createFileRoute } from "@tanstack/react-router";
import { alternateLinkList } from "@/lib/i18n/locale";
import { Binoculars, MapPin, Sparkles, Mail, Workflow, ListTodo, RefreshCcw } from "lucide-react";
import { AiGeneratorPage } from "@/components/marketing/AiGeneratorPage";

export const Route = createFileRoute("/vi/ai/lead-finder")({
  head: () => ({
    meta: [
      { title: "Tìm khách hàng tiềm năng bằng AI — Banana Cards" },
      { name: "description", content: "Tìm và làm giàu dữ liệu khách hàng tiềm năng với AI." },
    ],
    links: alternateLinkList("/ai/lead-finder", "vi"),
  }),
  component: Page,
});

const EXAMPLES = [
  "Thợ sửa ống nước ở TP.HCM có dưới 30 đánh giá Google và không có website",
  "Phòng khám nha khoa ở Hà Nội chưa có đặt lịch online",
  "Quán cà phê ở Đà Nẵng không có tài khoản Instagram",
  "Khách sạn boutique ở Hội An chưa có trên TripAdvisor",
  "Môi giới bất động sản ở Nha Trang có website cũ hơn 5 năm",
  "Studio yoga ở TP.HCM không có ứng dụng di động",
  "Văn phòng luật ở Hà Nội có dưới 50 đánh giá",
];

export function Page() {
  return (
    <AiGeneratorPage
      eyebrow="Tìm khách hàng bằng AI"
      title="Tìm 100 khách hàng"
      italic="tiếp theo."
      description="Mô tả khách hàng lý tưởng bằng tiếng Việt tự nhiên. Banana rà Google Maps, lọc theo tín hiệu (website, đánh giá, mạng xã hội) và trả về danh sách sạch — sẵn sàng làm giàu dữ liệu, liên hệ và đưa vào pipeline."
      headline="Bạn muốn tìm loại doanh nghiệp nào?"
      placeholders={EXAMPLES}
      linkedFromHref="/vi/platform/scout"
      linkedFromLabel="Banana Scout — AI tìm khách hàng"
      sections={[
        { icon: Sparkles, title: "Tìm bằng ngôn ngữ tự nhiên", desc: "Không cần bộ lọc phức tạp. 'Founder SaaS ở TP.HCM dưới 20 nhân viên' — chỉ vậy thôi." },
        { icon: MapPin, title: "Local + B2B toàn cầu", desc: "Dựa trên Google Maps + business graph. Tìm dịch vụ địa phương hay khách B2B quốc tế." },
        { icon: Binoculars, title: "Tín hiệu để lọc", desc: "Lọc theo số đánh giá, tuổi website, mạng xã hội, giờ mở cửa và hơn thế nữa." },
      ]}
      capabilities={[
        "Tìm trên Google Maps bằng tiếng Việt tự nhiên",
        "Lọc theo đánh giá, website, mạng xã hội, giờ mở",
        "Làm giàu email & số điện thoại theo từng lead",
        "Đẩy lead vào pipeline chỉ 1 chạm",
        "Tự động tạo công việc theo dõi từng lead",
        "Xuất hàng loạt ra CSV",
        "Loại bỏ trùng với CRM hiện có",
        "Lưu và chạy lại các tìm kiếm trước",
      ]}
      howItWorks={[
        { title: "Mô tả ICP của bạn", desc: "Ngành, khu vực và bất kỳ tín hiệu chọn lọc nào bạn quan tâm." },
        { title: "AI chạy tìm kiếm", desc: "Banana quét Google Maps và các danh bạ trong vài giây." },
        { title: "Xem & lọc", desc: "Mở từng lead, xem tín hiệu, làm giàu email và số điện thoại." },
        { title: "Đẩy vào CRM", desc: "Đưa lead vào pipeline và giao việc cho team." },
      ]}
      faq={[
        { q: "Nguồn dữ liệu lead từ đâu?", a: "Chủ yếu từ Google Maps / Google Places — cùng nguồn Google dùng cho tìm kiếm địa phương. Banana bổ sung dữ liệu từ business graph B2B (thông tin công ty, tech-stack, mạng xã hội) và nguồn web mở, nên mỗi lead đi kèm website, đánh giá, giờ mở và điểm chất lượng." },
        { q: "Việc này có hợp pháp không? GDPR và CCPA thì sao?", a: "Có — Banana chỉ hiển thị thông tin doanh nghiệp công khai từ Google Maps và web mở. Dữ liệu cá nhân được xử lý theo cơ sở lợi ích chính đáng cho mục đích B2B; cá nhân có thể yêu cầu xoá bất cứ lúc nào và bạn có thể tắt tra cứu ở các khu vực cụ thể." },
        { q: "Dữ liệu chính xác đến đâu?", a: "Tên, địa chỉ, số điện thoại, website và số đánh giá lấy trực tiếp từ Google, độ chính xác thường trên 95%. Các trường được làm giàu (email, người quyết định, tech-stack) đi kèm điểm tin cậy — giá trị tin cậy cao tự điền, giá trị thấp hơn được đánh dấu để kiểm tra." },
        { q: "Tôi có thể xuất lead không?", a: "Có — gói trả phí xuất ra CSV, đẩy vào pipeline Banana CRM, hoặc đồng bộ qua Zapier và webhook tới HubSpot, Pipedrive, Salesforce và hơn 4.000 ứng dụng." },
        { q: "Khác gì so với ZoomInfo hay Apollo?", a: "Apollo và ZoomInfo tốt cho prospecting SaaS enterprise nhưng đắt và yếu với SMB địa phương. Banana Scout dành cho founder, agency, freelancer và team sales địa phương cần tìm SMB nhanh, bằng tiếng Việt tự nhiên và giá rẻ hơn nhiều." },
      ]}
      outroCta={{ to: "/signup", label: "Bắt đầu miễn phí" }}
      flow={{
        labels: {
          next: "Tiếp",
          back: "Quay lại",
          generate: "Tìm ngay",
          step: "Bước {n} / {total}",
          nextThingsHeading: "Bạn còn có thể làm gì",
        },
        steps: [
          {
            question: "Loại doanh nghiệp nào?",
            multi: false,
            options: ["Dịch vụ địa phương (thợ, nha khoa…)", "Nhà hàng & lưu trú", "Bán lẻ & boutique", "Bất động sản", "Sức khoẻ & fitness", "Dịch vụ chuyên nghiệp (luật, kế toán)", "B2B khác"],
          },
          {
            question: "Tìm ở đâu?",
            multi: false,
            options: ["Việt Nam", "Đông Nam Á", "Châu Á", "Châu Âu", "Bắc Mỹ", "Toàn cầu"],
          },
          {
            question: "Tín hiệu nào quan trọng?",
            options: ["Chưa có website", "Ít đánh giá", "Chưa có đặt lịch online", "Website cũ", "Không có mạng xã hội", "Đội nhỏ"],
          },
        ],
        loadingMessages: [
          "Đang quét Google Maps…",
          "Đang đối chiếu business graph…",
          "Đang áp dụng bộ lọc…",
          "Đang làm giàu thông tin liên hệ…",
          "Đang chấm điểm khách hàng tiềm năng…",
          "Đang loại bỏ trùng với CRM…",
          "Đang chuẩn bị xuất dữ liệu…",
        ],
        readyTitle: "Danh sách khách hàng của bạn đã sẵn sàng.",
        readyBody: "Chúng tôi tìm thấy khoảng 140 khách hàng tiềm năng phù hợp với ICP của bạn. Đăng ký để xem danh sách đầy đủ, làm giàu email + số điện thoại và đẩy thẳng vào pipeline.",
        ctaLabel: "Đăng ký để xem lead",
        ctaHref: "https://app.banana.cards/signup",
        nextThings: [
          { icon: Mail, title: "Làm giàu email & SĐT", desc: "1 chạm để lấy thông tin liên hệ đã xác minh." },
          { icon: Workflow, title: "Đẩy vào pipeline CRM", desc: "Chuyển hàng loạt lead vào pipeline Banana." },
          { icon: ListTodo, title: "Tự giao việc follow-up", desc: "Tạo công việc cho từng lead và chia cho team." },
          { icon: RefreshCcw, title: "Lưu & chạy lại", desc: "Nhận lead mới hàng tuần tự động." },
        ],
      }}
    />
  );
}